import React, {useCallback} from 'react';

import EntryActions from 'actions/EntryActions';
import BenchlingConfig from 'BenchlingConfig';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {areAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import CreateEntryFromTemplateButtonBase, {
  CreateEntryFromTemplateSubmission,
} from 'notebook/entry-templates/CreateEntryFromTemplateButtonBase';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {VersionBase} from 'notebook/store/VersionedItemSubstore';
import {
  TemplatePublishingRecordsQuery,
  TemplatePublishingRecordsQueryVariables,
} from 'template-collectibles/publishing-ui/graphql/__generated/TemplatePublishingRecordsQuery';
import {TEMPLATE_PUBLISHING_RECORDS_QUERY} from 'template-collectibles/publishing-ui/graphql/TemplatePublishingRecordsQuery';
import {getEffectivePublishingRecord} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

export const CREATE_ENTRY_BUTTON_TEXT = 'Create entry';
export const CREATE_TEST_ENTRY_BUTTON_TEXT = 'Create test entry';

export const ARCHIVED_TEMPLATE_MESSAGE = 'Unarchive this template to create entries';

export const LATEST_VERSION_REQUIRED_MESSAGE =
  'To create an entry, please select the latest version of this template';

export const EFFECTIVE_VERSION_REQUIRED_MESSAGE = 'Select the effective version to create entries';

export const NO_EFFECTIVE_VERSION_MESSAGE = 'Publish a version to create entries';

export const LATEST_DRAFT_VERSION_REQUIRED_MESSAGE = 'Select the latest draft to create test entries';

export const TEMPLATE_STATUS_UNAVAILABLE_MESSAGE =
  'Template status is unavailable. Please wait for the template to finish loading';

/**
 * Returns the reason the effective version requirement blocks entry creation, or `null` if it doesn't. Only
 * meaningful when template publishing is enabled: entries are then created from the effective version, which
 * may sit underneath newer drafts or may not exist at all.
 */
function getEffectiveVersionMessage(
  result: SkippableBenchlingQueryResult<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >,
  openVersionApiId: string | undefined
): string | null {
  if (
    result.type !== QueryResultType.SUCCESS ||
    isInaccessible(result.data.template) ||
    !areAllAccessible(result.data.template.publishingRecords)
  ) {
    return TEMPLATE_STATUS_UNAVAILABLE_MESSAGE;
  }
  const effectiveRecord = getEffectivePublishingRecord(result.data.template.publishingRecords);
  if (effectiveRecord == null) {
    return NO_EFFECTIVE_VERSION_MESSAGE;
  }
  return effectiveRecord.publishableVersionId === openVersionApiId
    ? null
    : EFFECTIVE_VERSION_REQUIRED_MESSAGE;
}

function getIsDraftVersion(
  result: SkippableBenchlingQueryResult<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >,
  openVersionApiId: string | undefined
): boolean | null {
  if (
    openVersionApiId == null ||
    result.type !== QueryResultType.SUCCESS ||
    isInaccessible(result.data.template) ||
    !areAllAccessible(result.data.template.publishingRecords)
  ) {
    return null;
  }
  return !result.data.template.publishingRecords.some(
    (record) => record.publishableVersionId === openVersionApiId
  );
}

function useLatestVersionApiId(templateId: string): string | undefined {
  const selectLatestVersionApiId = useCallback(
    (store: typeof TemplateStore): string | undefined => {
      if (!store.inStore(templateId)) {
        return undefined;
      }
      const {fetchSuccess, versions} = store.getVersionState(templateId);
      if (!fetchSuccess) {
        return undefined;
      }
      const latestVersion: VersionBase | undefined = versions[versions.length - 1];
      return latestVersion == null
        ? undefined
        : (latestVersion.apiIdentifier ?? latestVersion.api_identifier);
    },
    [templateId]
  );
  return useStoreSelector(TemplateStore, selectLatestVersionApiId);
}

function handleCreateEntryFromTemplate({
  entryName,
  folderId,
  templateId,
  tagSchemaId,
  schemaTags,
  initialDay,
  studyAssociations,
}: CreateEntryFromTemplateSubmission): void {
  EntryActions.createFromTemplate({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
    /* eslint-disable-next-line @typescript-eslint/unbound-method */
    onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
  });
}

function handleCreateEntryFromDraftTemplate({
  entryName,
  folderId,
  templateId,
  tagSchemaId,
  schemaTags,
  initialDay,
  studyAssociations,
}: CreateEntryFromTemplateSubmission): void {
  EntryActions.createFromDraftTemplate({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
    /* eslint-disable-next-line @typescript-eslint/unbound-method */
    onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
  });
}

interface Props {
  templateId: string;
  isTemplateArchived: boolean;
  /**
   * API identifier of the template version currently open in the editor, or `undefined` before the version
   * list has loaded.
   */
  openVersionApiId: string | undefined;
  isViewingLatestVersion: boolean;
  canCreateEntriesFromDraftTemplates?: boolean;
}

/**
 * Opens the create-entry modal for the version of the template being viewed.
 *
 * Published versions create normal entries from the effective version. Users with draft entry creation
 * access can create test entries from the latest draft. The action is disabled when the open version does
 * not match the version used for its entry type, or when the template is archived.
 */
export default function CreateEntryFromTemplateButton({
  templateId,
  isTemplateArchived,
  openVersionApiId,
  isViewingLatestVersion,
  canCreateEntriesFromDraftTemplates = false,
}: Props): JSX.Element {
  const isPublishingEnabled = BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING;
  const publishingRecordsResult = useSkippableBenchlingQuery<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >(TEMPLATE_PUBLISHING_RECORDS_QUERY, {variables: {templateId}, skip: !isPublishingEnabled});

  const latestVersionApiIdFromStore = useLatestVersionApiId(templateId);
  const latestVersionApiId = isViewingLatestVersion ? openVersionApiId : latestVersionApiIdFromStore;
  const isDraftEntryCreationAvailable =
    BenchlingConfig.ELN_ENABLE_TEMPLATE_REVIEWS && canCreateEntriesFromDraftTemplates;
  const isOpenVersionDraft = getIsDraftVersion(publishingRecordsResult, openVersionApiId);
  const isLatestVersionDraft = getIsDraftVersion(publishingRecordsResult, latestVersionApiId);
  const isViewingEligibleDraft =
    isDraftEntryCreationAvailable && isOpenVersionDraft === true && isLatestVersionDraft === true;
  const isTemplateStatusUnavailable =
    isPublishingEnabled &&
    (isOpenVersionDraft == null || (isDraftEntryCreationAvailable && isLatestVersionDraft == null));

  // Being archived blocks entry creation outright, so it takes precedence over the version reasons:
  // pointing an archived template's reader at a different version would not get them any closer.
  let disabledMessage: string | null;
  if (isTemplateArchived) {
    disabledMessage = ARCHIVED_TEMPLATE_MESSAGE;
  } else if (isTemplateStatusUnavailable) {
    disabledMessage = TEMPLATE_STATUS_UNAVAILABLE_MESSAGE;
  } else if (isViewingEligibleDraft) {
    disabledMessage = isViewingLatestVersion ? null : LATEST_DRAFT_VERSION_REQUIRED_MESSAGE;
  } else if (isPublishingEnabled) {
    disabledMessage = getEffectiveVersionMessage(publishingRecordsResult, openVersionApiId);
  } else {
    disabledMessage = isViewingLatestVersion ? null : LATEST_VERSION_REQUIRED_MESSAGE;
  }

  return (
    <CreateEntryFromTemplateButtonBase
      buttonId={isViewingEligibleDraft ? 'create-test-entry-button' : 'create-entry-button'}
      buttonText={isViewingEligibleDraft ? CREATE_TEST_ENTRY_BUTTON_TEXT : CREATE_ENTRY_BUTTON_TEXT}
      disabledMessage={disabledMessage}
      filterTemplateIds={isViewingEligibleDraft ? [templateId] : undefined}
      onRequestSubmit={
        isViewingEligibleDraft ? handleCreateEntryFromDraftTemplate : handleCreateEntryFromTemplate
      }
      showUnpublishedCollectibles={isViewingEligibleDraft ? true : undefined}
      templateId={templateId}
    />
  );
}
